import { jurisdictionValueToLabel } from "./jurisdiction/structuredJurisdiction";

const GUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function looksLikeGuid(value: string): boolean {
  return GUID_REGEX.test(value.trim());
}

function trimToText(value: unknown): string {
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return "";
}

function formatJurisdictionNames(jurisdictionNames: unknown): string {
  if (jurisdictionNames == null) {
    return "";
  }
  if (Array.isArray(jurisdictionNames)) {
    return jurisdictionNames
      .filter((x) => x != null && String(x).trim() !== "")
      .map(String)
      .join(", ");
  }
  if (typeof jurisdictionNames === "string") {
    return jurisdictionNames.trim();
  }
  return "";
}

export type FirmJurisdictionSource = {
  /** Structured object, legacy label or legacy GUID. */
  jurisdiction?: unknown;
  jurisdictionName?: string;
  jurisdictionNames?: unknown;
  legacyJurisdictionId?: string;
};

/** Prefer human-readable jurisdiction; ignore GUIDs stored in denormalized fields. */
export function resolveFirmJurisdictionDisplay(
  fromRegistration: unknown,
  firm?: FirmJurisdictionSource,
): string {
  const fromReg = jurisdictionValueToLabel(fromRegistration);
  if (fromReg && !looksLikeGuid(fromReg)) {
    return fromReg;
  }

  const fromName = trimToText(firm?.jurisdictionName);
  if (fromName && !looksLikeGuid(fromName)) {
    return fromName;
  }

  const fromJ = jurisdictionValueToLabel(firm?.jurisdiction);
  if (fromJ && !looksLikeGuid(fromJ)) {
    return fromJ;
  }

  const fromNames = formatJurisdictionNames(firm?.jurisdictionNames);
  if (fromNames && !looksLikeGuid(fromNames)) {
    return fromNames;
  }

  return "";
}

export function collectDistinctJurisdictionOptions(
  sources: FirmJurisdictionSource[],
): { id: string; name: string }[] {
  const byLabel = new Map<string, string>();

  for (const source of sources) {
    const label = resolveFirmJurisdictionDisplay(source.jurisdiction, source);
    if (!label) {
      continue;
    }

    const key = label.toLocaleLowerCase();
    if (!byLabel.has(key)) {
      byLabel.set(key, label);
    }
  }

  return [...byLabel.values()]
    .map((name) => ({ id: name, name }))
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
}

export function buildJurisdictionGuidCatalog(
  sources: FirmJurisdictionSource[],
): { id: string; name: string }[] {
  const byId = new Map<string, string>();

  for (const source of sources) {
    const raw =
      typeof source.jurisdiction === "string"
        ? source.jurisdiction
        : source.legacyJurisdictionId;
    const id = String(raw ?? "").trim();
    if (!id || !looksLikeGuid(id)) {
      continue;
    }

    const label = resolveFirmJurisdictionDisplay(source.jurisdiction, source);
    if (!label || byId.has(id)) {
      continue;
    }

    byId.set(id, label);
  }

  return [...byId.entries()].map(([id, name]) => ({ id, name }));
}

export function resolveFirmJurisdictionLabel(
  value: unknown,
  jurisdictionCatalog?: { id: string; name: string }[],
): string {
  if (value != null && typeof value === "object") {
    return jurisdictionValueToLabel(value);
  }
  const trimmed = trimToText(value);
  if (!trimmed) {
    return "";
  }
  if (!looksLikeGuid(trimmed)) {
    return trimmed;
  }
  const match = jurisdictionCatalog?.find((j) => j.id === trimmed);
  return trimToText(match?.name) || trimmed;
}
